import React, { useEffect, useState } from "react";
import algoliasearch from "algoliasearch";
import {
  InstantSearch,
  Hits,
  RefinementList,
  ClearRefinements,
  Stats,
  SearchBox,
  Configure,
  useRefinementList
} from "react-instantsearch";
import { createStore, applyMiddleware } from "redux";
import { Provider, connect } from "react-redux";
import thunk from "redux-thunk";
import { Button } from "react-bootstrap";

import CustomAudioPlayer from "components/CustomAudioPlayer";
import rootReducer from "../reducers";
import { fetchAllCartItems, addCartItemRequest, cartItemIds, addPreview } from "../actions/index";

const searchClient = algoliasearch(window.algoliaConfig.appId, window.algoliaConfig.searchApiKey);
const indexName = window.algoliaConfig.indexName;

// Create Redux store
const store = createStore(rootReducer, applyMiddleware(thunk));

// Initialize cart items
store.dispatch(fetchAllCartItems());

// Create a separate component to access refinement state without conflicts
const GenreRefinementContext = React.createContext();

const GenreRefinementWrapper = ({ children, preSelectedGenre }) => {
  const { items: genreItems, refine: refineGenre } = useRefinementList({
    attribute: 'genres'
  });

  // Auto-select the genre when component mounts
  useEffect(() => {
    if (preSelectedGenre && genreItems.length > 0) {
      const genreItem = genreItems.find(item =>
        item.value.toLowerCase().replace(/\s+/g, '-') === preSelectedGenre.toLowerCase()
      );

      if (genreItem && !genreItem.isRefined) {
        refineGenre(genreItem.value);
      }
    }
  }, [preSelectedGenre, genreItems, refineGenre]);

  return (
    <GenreRefinementContext.Provider value={{ genreItems, refineGenre }}>
      {children}
    </GenreRefinementContext.Provider>
  );
};

// Hit component with genre functionality
const HitWithRefinement = connect(state => ({
  cart_items_ids: cartItemIds(state.cart_items)
}))(({ hit, dispatch, cart_items_ids, ownedProductIds }) => {
  const genreContext = React.useContext(GenreRefinementContext);

  const handleGenreClick = React.useCallback((genre, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (genreContext && genreContext.refineGenre) {
      genreContext.refineGenre(genre);
    }
  }, [genreContext]);

  const isGenreSelected = React.useCallback((genre) => {
    if (!genreContext || !genreContext.genreItems) return false;
    return genreContext.genreItems.some(item => item.value === genre && item.isRefined);
  }, [genreContext]);

  return (
    <Hit
      hit={hit}
      dispatch={dispatch}
      cart_items_ids={cart_items_ids}
      ownedProductIds={ownedProductIds}
      onGenreClick={handleGenreClick}
      isGenreSelected={isGenreSelected}
    />
  );
});

// Hit component (same as Main.js)
const Hit = ({ hit, dispatch, cart_items_ids, ownedProductIds, onGenreClick, isGenreSelected }) => {
  const [isAdding, setIsAdding] = useState(false);
  const productId = hit.objectID || hit.id;
  const isInCart = cart_items_ids.includes(productId);
  const isOwned = ownedProductIds.includes(productId);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    try {
      await dispatch(addCartItemRequest(productId));
    } catch (error) {
      console.error("Failed to add item to cart:", error);
    } finally {
      setIsAdding(false);
    }
  };

  const handlePlayPreview = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!hit.audio_preview_url) {
      return;
    }

    const previewData = {
      id: productId,
      name: hit.name,
      preview: hit.audio_preview_url,
      images: {
        mid: hit.mid_image
      },
      manufacturer_name: hit.manufacturer_name || 'Unknown'
    };

    dispatch(addPreview(previewData));
  };

  const handleCardClick = (e) => {
    if (e.target.closest('.pack-cart-button') || e.target.closest('.preset-pack-image')) {
      return;
    }
    e.preventDefault();
    window.location.href = `/preset_packs/${hit.slug}`;
  };

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (hit.audio_preview_url) {
      handlePlayPreview(e);
    } else {
      window.location.href = `/preset_packs/${hit.slug}`;
    }
  };

  return (
    <div className="preset-pack-card" data-card="true" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div
        className={`preset-pack-image ${hit.audio_preview_url ? 'clickable-audio' : 'clickable-nav'}`}
        onClick={handleImageClick}
        title={hit.audio_preview_url ? 'Click to play preview' : 'Click to view details'}
      >
        {hit.mid_image && (
          <img src={hit.mid_image} alt={hit.name} className="pack-artwork" />
        )}
        {hit.audio_preview_url && (
          <div className="audio-indicator">
            <i className="fa fa-play"></i>
          </div>
        )}
        {/* Special badges for exclusive, featured, new - overlaid on image */}
        {(hit.exclusive || hit.featured || hit.is_new) && (
          <div className="pack-badges-overlay">
            {hit.exclusive && <span className="badge badge-exclusive">Exclusive</span>}
            {hit.featured && <span className="badge badge-featured">Featured</span>}
            {hit.is_new && <span className="badge badge-new">New</span>}
          </div>
        )}
      </div>
      <div className="preset-pack-content">
        <div className="pack-header">
          <h4 className="pack-title">{hit.name}</h4>
          {hit.manufacturer_name && (
            <p className="pack-manufacturer">by {hit.manufacturer_name}</p>
          )}
        </div>

        {hit.genres && hit.genres.length > 0 && (
          <div className="genre-list">
            {hit.genres.slice(0, 4).join(', ')}
            {hit.genres.length > 4 && (
              <span className="genre-more"> +{hit.genres.length - 4} more</span>
            )}
          </div>
        )}

        {(hit.serum_1_presets || hit.serum_2_presets) && (
          <div className="preset-info">
            {hit.serum_1_presets && (
              <span className="preset-count">{hit.serum_1_presets} Serum 1</span>
            )}
            {hit.serum_2_presets && (
              <span className="preset-count">{hit.serum_2_presets} Serum 2</span>
            )}
          </div>
        )}

        <div className="pack-actions">
          {isAdding ? (
            <Button className="pack-cart-button" variant="secondary" disabled>
              <i className="fa fa-spinner fa-spin"></i>
            </Button>
          ) : (isOwned || isInCart) ? (
            <Button className="pack-cart-button" variant="success" disabled>
              <i className="fa fa-check"></i>
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              className="pack-cart-button"
              variant="primary"
            >
              ${hit.price || '0.00'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main GenrePage component
const GenrePage = (props) => {
  const [ownedProductIds, setOwnedProductIds] = useState([]);
  const genre = props.genre; // Genre slug from URL

  useEffect(() => {
    const initialIds = props.ownedProductIds || [];
    setOwnedProductIds(initialIds.map(String));
  }, [props.ownedProductIds]);

  useEffect(() => {
    const handleCartUpdate = () => {
      console.log("GenrePage: Cart updated, refreshing cart data");
    };

    window.addEventListener('cart-updated', handleCartUpdate);

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, []);

  // Convert slug back to display name
  const genreDisplayName = genre ? genre.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';

    return (
    <Provider store={store}>
      <InstantSearch searchClient={searchClient} indexName={indexName}>
        <Configure hitsPerPage={20} filters="active:true" />
        <GenreRefinementWrapper preSelectedGenre={genre}>
          <div className="preset-pack-container">
            {/* Page Header */}
            <div className="search-header">
              <h2 className="page-title">{genreDisplayName} Preset Packs</h2>
              <SearchBox
                placeholder="Search preset packs..."
                className="preset-search-box"
              />
              <Stats className="search-stats" />
            </div>

            <div className="preset-pack-layout">
              {/* Left Sidebar with Facets */}
              <div className="facets-sidebar">

                {/* Genre Facet */}
                <div className="facet-section">
                  <h4 className="facet-title">Genres</h4>
                  <RefinementList
                    attribute="genres"
                    limit={10}
                    showMore={true}
                    showMoreLimit={20}
                    className="genre-refinement"
                  />
                </div>

                {/* Manufacturer Facet */}
                <div className="facet-section">
                  <h4 className="facet-title">Manufacturer</h4>
                  <RefinementList
                    attribute="manufacturer_name"
                    limit={10}
                    showMore={true}
                    showMoreLimit={20}
                    className="manufacturer-refinement"
                  />
                </div>

                {/* Plugin Name Facet */}
                <div className="facet-section">
                  <h4 className="facet-title">Plugin</h4>
                  <RefinementList
                    attribute="plugin_name"
                    limit={10}
                    className="plugin-refinement"
                  />
                </div>

                <ClearRefinements
                  className="clear-filters-btn"
                />
              </div>

              {/* Main Content Area */}
              <div className="preset-packs-main">
                <div className="preset-packs-grid">
                  <Hits
                    hitComponent={(props) => <HitWithRefinement {...props} ownedProductIds={ownedProductIds} />}
                    transformItems={(items) => {
                      // Sort items to put owned products at the end
                      const unownedItems = items.filter(item => !ownedProductIds.includes(item.objectID || item.id));
                      const ownedItems = items.filter(item => ownedProductIds.includes(item.objectID || item.id));
                      return [...unownedItems, ...ownedItems];
                    }}
                  />
                </div>
              </div>
            </div>

            <CustomAudioPlayer />
          </div>
        </GenreRefinementWrapper>
      </InstantSearch>
    </Provider>
  );
};

export default GenrePage;